import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button, Checkbox, FormControlLabel } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { ColDef, IDoesFilterPassParams, IFilterParams } from 'ag-grid-community'
import clsx from 'clsx'
import moment from 'moment'
import {
  clearFilteredDateGroupOptions,
  clearFilteredDateValues,
  DateFilterValue,
  getFilteredDateGroupOptions,
  getFilteredDateValue,
  setFilteredDateGroupOptions,
  setFilteredDateValue,
} from 'cache/date-filter'
import { DATE_FORMATS } from 'constants/dateFormats'
import { formatDateWithoutTime } from '../utils/date'
import { defaultDateFilterOptions } from './helperFns'
import styles from './styles.module.scss'

interface CustomDateFilterProps extends IFilterParams {
  colDef: ColDef
}
interface DateFilterOption {
  key: string
  label: string
  selected: boolean
  hasData?: boolean
}

interface CustomDateFilterModel {
  filterType: 'customDateFilter'
  date: string | null
  groupOptions: DateFilterOption[]
}

interface CustomVanillaDateFilterRef {
  doesFilterPass: (params: IDoesFilterPassParams<any>) => boolean
  isFilterActive: () => boolean
  afterGuiAttached: () => void
  getModel: () => CustomDateFilterModel | null
  setModel: (model: CustomDateFilterModel | null) => void
}

const isValidDate = (value: unknown): value is Date => {
  return value instanceof Date && !Number.isNaN(value.getTime())
}

export const CustomVanillaDateFilterRenderer = forwardRef<
  CustomVanillaDateFilterRef,
  CustomDateFilterProps
>((props, ref) => {
  const pickerDateFormat = DATE_FORMATS.FILTER_FORMAT
  const filterDateFormat = DATE_FORMATS.DISPLAY_FORMAT
  const { colDef, filterChangedCallback } = props
  const colId = (colDef?.colId ?? colDef?.field) as string
  const filteredDateValue = getFilteredDateValue(colId)

  const getInitialDate = (value: DateFilterValue): Date | null => {
    if (!value) {
      return null
    }
    if (isValidDate(value)) {
      return value
    }
    const parsed = moment(value)
    return parsed.isValid() ? parsed.toDate() : null
  }

  const initialDate = getInitialDate(filteredDateValue)

  const [date, setDate] = useState<Date | null>(initialDate)
  const [isShowPicker, setIsShowPicker] = useState(false)
  const textRef = useRef<HTMLInputElement>(null)
  const filteredOptions = getFilteredDateGroupOptions(colId)
  const [groupOptions, setGroupOptions] = useState(
    filteredOptions.length ? filteredOptions : defaultDateFilterOptions()
  )
  const MONTH_MIDPOINT_DAY = 15 // Using 15th as standard month midpoint

  const dateObj = useMemo(() => {
    const yesterday = moment().subtract(1, 'day').format(filterDateFormat)
    const today = moment().format(filterDateFormat)
    const startOfMonth = moment().startOf('month').format(filterDateFormat)
    const startOfYear = moment().startOf('year').format(filterDateFormat)
    const lastMonthEndDate = moment().subtract(1, 'months').endOf('month').format(filterDateFormat)
    const startOfNextYear = moment().add(1, 'years').startOf('year').format(filterDateFormat)
    const startOfLastWeek = moment().subtract(1, 'weeks').startOf('week').format(filterDateFormat)
    const endOfLastWeek = moment().subtract(1, 'weeks').endOf('week').format(filterDateFormat)
    const startOfNextWeek = moment().add(1, 'weeks').startOf('week').format(filterDateFormat)
    const endOfNextWeek = moment().add(1, 'weeks').endOf('week').format(filterDateFormat)
    const startOfNextMonth = moment().add(1, 'month').startOf('month').format(filterDateFormat)
    const startOfCurrMonth = moment().startOf('month').format(filterDateFormat)
    const startOfThisWeek = moment().startOf('week').format(filterDateFormat)
    const endOfYear = moment().endOf('year').format(filterDateFormat)
    const midDate = `${moment().format('YYYY')}-${moment().format('MM')}-${MONTH_MIDPOINT_DAY}`
    const endOfThisMonth = moment().endOf('month').format(filterDateFormat)

    return {
      yesterday,
      today,
      startOfMonth,
      startOfYear,
      lastMonthEndDate,
      startOfNextYear,
      startOfLastWeek,
      endOfLastWeek,
      startOfNextWeek,
      endOfNextWeek,
      startOfCurrMonth,
      startOfThisWeek,
      startOfNextMonth,
      endOfYear,
      midDate,
      endOfThisMonth,
    }
  }, [filterDateFormat])

  const rowFilterCondition = useCallback(
    (key: any, date: any) => {
      let isMatch = false
      switch (key) {
        case 'today':
          isMatch = moment(date).isSame(dateObj.today)
          break
        case 'yesterday':
          isMatch = moment(date).isSame(dateObj.yesterday)
          break
        case 'earlierThisYear':
          isMatch = moment(date).isBetween(
            dateObj.startOfYear,
            dateObj.lastMonthEndDate,
            undefined,
            '[]'
          )
          break
        case 'earlierThisMonth':
          isMatch = moment(date).isBetween(
            dateObj.startOfCurrMonth,
            dateObj.yesterday,
            undefined,
            '[]'
          )
          break
        case 'earlierThisWeek':
          isMatch = moment(date).isBetween(
            dateObj.startOfThisWeek,
            dateObj.yesterday,
            undefined,
            '[]'
          )
          break
        case 'laterThisYear':
          isMatch = moment(date).isBetween(
            dateObj.startOfNextMonth,
            dateObj.endOfYear,
            undefined,
            '[]'
          )
          break
        case 'laterThisMonth':
          isMatch = moment(date).isBetween(dateObj.midDate, dateObj.endOfThisMonth, undefined, '[]')
          break
        case 'lastWeek':
          isMatch = moment(date).isBetween(
            dateObj.startOfLastWeek,
            dateObj.endOfLastWeek,
            undefined,
            '[]'
          )
          break
        case 'nextWeek':
          isMatch = moment(date).isBetween(
            dateObj.startOfNextWeek,
            dateObj.endOfNextWeek,
            undefined,
            '[]'
          )
          break
        case 'priorThisYear':
          isMatch = moment(date).isBefore(dateObj.startOfYear)
          break
        case 'beyondThisYear':
          isMatch = moment(date).isSameOrAfter(dateObj.startOfNextYear)
          break
      }
      return isMatch
    },
    [dateObj]
  )

  const isRowsFilters = (row: any) => {
    let isMatch = false
    const colData = colId ? row[colId] : undefined
    const colDate = formatDateWithoutTime(colData)
    if (!date) {
      for (const dateOption of groupOptions) {
        if (dateOption.selected && rowFilterCondition(dateOption.key, colDate)) {
          isMatch = true
          break
        }
      }
    } else if (colData && date) {
      const colMoment = moment(colData)
      const pickedMoment = moment(date)
      if (colMoment.isValid() && pickedMoment.isValid()) {
        isMatch = colMoment.isSame(pickedMoment, 'day')
      }
    }
    return isMatch
  }

  const clearGroupOptions = () => {
    const cloneOtions = [...groupOptions]
    setGroupOptions([
      ...cloneOtions.map((o: any) => ({
        ...o,
        selected: false,
      })),
    ])
    clearFilteredDateGroupOptions(colId)
  }

  const clearFilter = () => {
    clearGroupOptions()
    setDate(null)
    clearFilteredDateValues(colId)
  }

  const isFilterPass = () => {
    if (date) {
      return true
    }
    const findSection = groupOptions.filter((o: any) => o.selected)
    return findSection.length > 0
  }

  const getModel = (): CustomDateFilterModel | null => {
    if (!isFilterPass()) {
      return null
    }

    const modelDate = isValidDate(date) ? date.toISOString() : null

    return {
      filterType: 'customDateFilter',
      date: modelDate,
      groupOptions,
    }
  }

  const setModel = (model: CustomDateFilterModel | null) => {
    if (!model) {
      clearFilter()
      return
    }

    const { date: modelDate, groupOptions: modelGroupOptions } = model

    const parsedDate = modelDate ? new Date(modelDate) : null
    const safeDate = isValidDate(parsedDate) ? parsedDate : null
    setDate(safeDate)
    setFilteredDateValue(colId, safeDate)

    if (Array.isArray(modelGroupOptions)) {
      setGroupOptions(modelGroupOptions)
      setFilteredDateGroupOptions(colId, modelGroupOptions)
    }
  }

  useImperativeHandle(ref, (): CustomVanillaDateFilterRef => {
    return {
      doesFilterPass(params: IDoesFilterPassParams<any>) {
        return isRowsFilters(params.data)
      },

      isFilterActive() {
        return isFilterPass()
      },

      afterGuiAttached() {
        if (!isShowPicker) {
          textRef?.current?.blur()
        }
      },

      getModel,
      setModel,
    }
  })

  useEffect(() => {
    if (filterChangedCallback && typeof filterChangedCallback === 'function') {
      filterChangedCallback()
    }
  }, [groupOptions, date, filterChangedCallback])

  const rows = useMemo(() => {
    const rowData: any[] = []
    const { api } = props
    api.forEachNode((node: any) => rowData.push(node.data))
    return rowData
  }, [props])

  const visibleOptions = useMemo(
    () =>
      groupOptions.map((option: any) => {
        const hasData = rows.some((row: any) => {
          const colData = row[colId]
          const rowDate = formatDateWithoutTime(colData)
          return rowDate ? rowFilterCondition(option.key, rowDate) : false
        })

        return {
          ...option,
          hasData,
        }
      }),
    [groupOptions, rows, colId, rowFilterCondition]
  )

  return (
    <div className={clsx(styles.container)}>
      <div className={clsx(styles.groupOptions)}>
        {visibleOptions.map((dateO: any, index: number) => {
          if (!dateO.hasData) {
            return null
          }

          return (
            <div key={dateO.key} className={clsx(styles.childDate)}>
              <FormControlLabel
                className={clsx(styles.checkbox)}
                control={
                  <Checkbox
                    onChange={(e: any) => {
                      const cloneOtions = [...groupOptions]
                      cloneOtions[index] = {
                        ...cloneOtions[index],
                        selected: e.target.checked,
                      }
                      setGroupOptions([...cloneOtions])
                      setFilteredDateGroupOptions(colId, [...cloneOtions])
                      setDate(null)
                      clearFilteredDateValues(colId)
                      setIsShowPicker(false)
                    }}
                    checked={dateO.selected}
                  />
                }
                label={dateO.label}
              />
            </div>
          )
        })}
      </div>
      <div className={clsx(styles.filterDate)}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            className={clsx(styles.specificTextfield)}
            orientation="portrait"
            inputRef={textRef}
            format={pickerDateFormat}
            value={date}
            label="Filter By Specific Date"
            onChange={(newDate: Date | null) => {
              clearGroupOptions()
              setDate(newDate)
              setFilteredDateValue(colId, newDate)
              setIsShowPicker(false)
            }}
            slotProps={{
              popper: {
                className: 'ag-custom-component-popup',
                placement: 'bottom-start',
                style: { zIndex: 9999 },
              },
              actionBar: {
                actions: [],
              },
              textField: {
                InputLabelProps: {
                  shrink: true,
                },
                inputProps: {
                  readOnly: true,
                },
              },
            }}
          />
        </LocalizationProvider>
      </div>
      <div className={clsx(styles.filterDateSelectorClr)}>
        <Button
          className={clsx(styles.clearBtn)}
          onClick={clearFilter}
          color="primary"
          variant="outlined"
        >
          Clear
        </Button>
      </div>
    </div>
  )
})

CustomVanillaDateFilterRenderer.displayName = 'CustomVanillaDateFilterRenderer'
